import { RichTextColumnNode, RichTextGridBlockNode } from '@cinemark/content-api-client';
import type { SerializedBlockNode } from '@payloadcms/richtext-lexical';
import clsx from 'clsx';
import { RichTextRenderer } from '../rich-text-renderer';

type RichTextGridBlockComponentProps = {
  node: SerializedBlockNode | undefined;
};

export const RichTextGridBlockComponent = ({ node }: RichTextGridBlockComponentProps): React.JSX.Element | null => {
  if (!node || node.type !== 'block' || !node.fields || typeof node.fields !== 'object' || node.fields.blockType !== 'grid') return null;

  const fields = node.fields as unknown as RichTextGridBlockNode;

  const getGapStyle = (gap: string): string => {
    switch (gap) {
      case 'xs':
        return 'gap-xs';
      case 'sm':
        return 'gap-sm';
      case 'ms':
        return 'gap-ms';
      case 'md':
        return 'gap-md';
      case 'ml':
        return 'gap-ml';
      case 'lg':
        return 'gap-lg';
      case 'xl':
        return 'gap-xl';
      case 'xxl':
        return 'gap-xxl';
      case 'jumbo':
        return 'gap-jumbo';
      default:
        return '';
    }
  };

  const getBaseColSpanClass = (width: string): string => {
    switch (width) {
      case 'auto':
        return 'col-auto';
      case '1/12':
        return 'col-span-1';
      case '2/12':
        return 'col-span-2';
      case '3/12':
        return 'col-span-3';
      case '4/12':
        return 'col-span-4';
      case '5/12':
        return 'col-span-5';
      case '6/12':
        return 'col-span-6';
      case '7/12':
        return 'col-span-7';
      case '8/12':
        return 'col-span-8';
      case '9/12':
        return 'col-span-9';
      case '10/12':
        return 'col-span-10';
      case '11/12':
        return 'col-span-11';
      case 'full':
        return 'col-span-12';
      default:
        return '';
    }
  };

  const getSmColSpanClass = (width: string): string => {
    switch (width) {
      case 'auto':
        return 'sm:col-auto';
      case '1/12':
        return 'sm:col-span-1';
      case '2/12':
        return 'sm:col-span-2';
      case '3/12':
        return 'sm:col-span-3';
      case '4/12':
        return 'sm:col-span-4';
      case '5/12':
        return 'sm:col-span-5';
      case '6/12':
        return 'sm:col-span-6';
      case '7/12':
        return 'sm:col-span-7';
      case '8/12':
        return 'sm:col-span-8';
      case '9/12':
        return 'sm:col-span-9';
      case '10/12':
        return 'sm:col-span-10';
      case '11/12':
        return 'sm:col-span-11';
      case 'full':
        return 'sm:col-span-12';
      default:
        return '';
    }
  };

  const getMdColSpanClass = (width: string): string => {
    switch (width) {
      case 'auto':
        return 'md:col-auto';
      case '1/12':
        return 'md:col-span-1';
      case '2/12':
        return 'md:col-span-2';
      case '3/12':
        return 'md:col-span-3';
      case '4/12':
        return 'md:col-span-4';
      case '5/12':
        return 'md:col-span-5';
      case '6/12':
        return 'md:col-span-6';
      case '7/12':
        return 'md:col-span-7';
      case '8/12':
        return 'md:col-span-8';
      case '9/12':
        return 'md:col-span-9';
      case '10/12':
        return 'md:col-span-10';
      case '11/12':
        return 'md:col-span-11';
      case 'full':
        return 'md:col-span-12';
      default:
        return '';
    }
  };

  const getLgColSpanClass = (width: string): string => {
    switch (width) {
      case 'auto':
        return '[&]:lg:col-auto';
      case '1/12':
        return '[&]:lg:col-span-1';
      case '2/12':
        return '[&]:lg:col-span-2';
      case '3/12':
        return '[&]:lg:col-span-3';
      case '4/12':
        return '[&]:lg:col-span-4';
      case '5/12':
        return '[&]:lg:col-span-5';
      case '6/12':
        return '[&]:lg:col-span-6';
      case '7/12':
        return '[&]:lg:col-span-7';
      case '8/12':
        return '[&]:lg:col-span-8';
      case '9/12':
        return '[&]:lg:col-span-9';
      case '10/12':
        return '[&]:lg:col-span-10';
      case '11/12':
        return '[&]:lg:col-span-11';
      case 'full':
        return '[&]:lg:col-span-12';
      default:
        return '';
    }
  };

  const getXlColSpanClass = (width: string): string => {
    switch (width) {
      case 'auto':
        return '[&]:xl:col-auto';
      case '1/12':
        return '[&]:xl:col-span-1';
      case '2/12':
        return '[&]:xl:col-span-2';
      case '3/12':
        return '[&]:xl:col-span-3';
      case '4/12':
        return '[&]:xl:col-span-4';
      case '5/12':
        return '[&]:xl:col-span-5';
      case '6/12':
        return '[&]:xl:col-span-6';
      case '7/12':
        return '[&]:xl:col-span-7';
      case '8/12':
        return '[&]:xl:col-span-8';
      case '9/12':
        return '[&]:xl:col-span-9';
      case '10/12':
        return '[&]:xl:col-span-10';
      case '11/12':
        return '[&]:xl:col-span-11';
      case 'full':
        return '[&]:xl:col-span-12';
      default:
        return '';
    }
  };

  const getResponsiveColSpanClass = (column: RichTextColumnNode): string => {
    const classes: string[] = [];

    const xsEffective = column.xsWidth || column.defaultWidth;
    const smEffective = column.smWidth || xsEffective;
    const mdEffective = column.mdWidth || smEffective;
    const lgEffective = column.lgWidth || mdEffective;
    const xlEffective = column.xlWidth || lgEffective;

    if (xsEffective) {
      classes.push(getBaseColSpanClass(xsEffective));
    }

    if (column.smWidth && smEffective !== xsEffective) {
      classes.push(getSmColSpanClass(smEffective));
    }

    if (column.mdWidth && mdEffective !== smEffective) {
      classes.push(getMdColSpanClass(mdEffective));
    }

    if (column.lgWidth && lgEffective !== mdEffective) {
      classes.push(getLgColSpanClass(lgEffective));
    }

    if (column.xlWidth && xlEffective !== lgEffective) {
      classes.push(getXlColSpanClass(xlEffective));
    }

    return classes.join(' ');
  };

  const getAlignmentStyle = (alignment: string): string => {
    switch (alignment) {
      case 'center':
        return 'items-center';
      case 'bottom':
        return 'items-end';
      default:
        return 'items-start';
    }
  };

  const getHorizontalAlignmentClass = (alignment: string | undefined): string => {
    if (!alignment) return '';

    switch (alignment) {
      case 'left':
        return 'justify-self-start';
      case 'center':
        return 'justify-self-center';
      case 'right':
        return 'justify-self-end';
      default:
        return '';
    }
  };

  const getInnerContentAlignmentClass = (alignment: string | undefined): string => {
    if (!alignment) return '';

    switch (alignment) {
      case 'left':
        return 'text-left';
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return '';
    }
  };

  return (
    <div className={clsx('grid grid-cols-12', getGapStyle(fields.gap), getAlignmentStyle(fields.alignment))}>
      {fields.columns?.map(column =>
        <div key={column.id}
          className={clsx('w-full', getResponsiveColSpanClass(column), getHorizontalAlignmentClass(column.horizontalAlignment),
            getInnerContentAlignmentClass(column.innerContentAlignment))}
        >
          <RichTextRenderer content={column.content} />
        </div>
      )}
    </div>
  );
};
